import { useMemo, useState, useEffect, useCallback } from 'react';
import { countdown, account } from '../state/index';
import { AlgorandLogo } from '../components/AlgorandLogo';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import useInterval from 'react-useinterval';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import BirthdayCountdown from '../components/countdown';
import { HFlex, VFlex } from '../components/layout';
import { observer } from 'mobx-react-lite';
import PreloadVideo from '../components/preloadvideo2022';
import { WalletButton } from '@txnlab/use-wallet-ui-react';
import dropClient from '../state/dropClient.js';

function Home({ dimStep, countdown }) {
  const [birthdayYear, setBirthdayYear] = useState(2026);
  const [nthBirthday, setNthBirthday] = useState('7th');
  // Server-side Turnstile verification status for the drop, fed by dropClient.
  // The widget itself is mounted app-wide (in TheApi); here we only show status.
  const [verifyStatus, setVerifyStatus] = useState(() =>
    dropClient.verified ? 'verified' : 'pending'
  );

  useEffect(() => {
    setVerifyStatus(dropClient.verified ? 'verified' : 'pending');
    dropClient.onStatus = setVerifyStatus;
    return () => {
      dropClient.onStatus = null;
    };
  }, []);

  const navigate = useNavigate();

  const gCalurl = useMemo(() => `https://calendar.google.com/calendar/render?action=TEMPLATE&text=Algorand%20MainNet%20${nthBirthday}%20Birthday&dates=${birthdayYear}0612T000000Z/${birthdayYear}0612T235900Z&details=Celebrate%20Algorand%20MainNet's%20${nthBirthday}%20Birthday%20with%20a%20commemorative%20NFT%20from%20IsItAlgorandsBirthday.com!%20Free%20if%20you%20are%20there%20at%20midnight%20UTC.%20And%20free%20fireworks%20for%20everyone%20during%20the%20big%20day!&location=https://isitalgorandsbirthday.com/&trp=true&ctz=utc`, [birthdayYear, nthBirthday]);

  useEffect(() => {
    setNthBirthday(countdown.ordinal);
    setBirthdayYear(countdown.from.year());
  }, [countdown.nth]);

  const addToGoogleCal = useCallback(() => {
    if (!window.open(gCalurl)) {
      navigate(gCalurl);
    }
  }, [navigate, gCalurl]);

  // The birthday instant (June 12, 00:00 UTC) shown in the visitor's local timezone.
  const birthdayLocal = countdown.from
    ? new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(countdown.from.toDate())
    : '';

  useEffect(() => {
    if (dimStep.birthday) {
      navigate(`/happy/birthday/${birthdayYear}`);
    }
	}, [dimStep.birthday, birthdayYear]);

  return <VFlex sx={{ flexGrow: 1}}>

    <VFlex>
      <Typography align="center" classes={{ root: dimStep.step > dimStep.breaks.h2 ? 'none' : 'fg-fade' }}>
        Algorand MainNet launched on{' '}
        <Link
          href="https://d13.co/posts/mainnet-archaeology-1-algorands-birthday/"
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          underline="always"
          sx={{ fontWeight: 600 }}
        >
          June 12th 2019
        </Link>.<br />
      </Typography>
    </VFlex>

    {countdown.connectOpen && (
    <VFlex
      sx={{
        mt: { xs: 2, lg: 0 },
        // On large screens, lift the eligibility dialog out of the page flow and
        // pin it to the top-right, directly under the header's Connect Wallet button.
        position: { lg: 'fixed' },
        top: { lg: 104 },
        right: { lg: 24 },
        alignItems: { lg: 'flex-end' },
        // Sit below the use-wallet-ui connected menu / connect modal (both z-index 50)
        // so the wallet dropdown opens over this card instead of behind it.
        zIndex: 40,
      }}
      className={dimStep.step > dimStep.breaks.h3 ? 'none' : 'fg-fade' }>
      <Box
        sx={{
          maxWidth: { xs: 520, lg: 360 },
          mx: 2,
          px: 2.5,
          py: 1.5,
          border: '1px solid #4FAC39',
          borderRadius: 1,
          backgroundColor: 'rgba(79, 172, 57, 0.08)',
          textAlign: 'center',
        }}
      >
        <Typography color="primary" sx={{ fontWeight: 600, mb: 0.5 }}>
          Midnight drop eligibility
        </Typography>
        <Typography variant="body2" align='left'>
          To be eligible for the free midnight drop: <br/><br/><b>1) Connect your wallet before midnight UTC.<br/>2) Be on the site when the countdown reaches zero.</b><br/><br/>You will receive a code in your browser that makes you eligible to claim the NFT for free.
        </Typography>
        <VFlex sx={{ mt: 1.5, gap: 1, alignItems: 'center' }}>
          
          {account.address ? (
            <VFlex sx={{ gap: 0.5, alignItems: 'center' }}>
              <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                {verifyStatus === 'verified'
                  ? "✓ Verified — you're registered for the drop"
                  : verifyStatus === 'failed'
                    ? 'Human verification failed — retrying…'
                    : countdown.dropWindowOpen
                      ? "Verifying you're human to register for the drop…"
                      : '✓ Wallet connected — keep this tab open; verification starts near midnight UTC'}
              </Typography>
            </VFlex>
          ) : <WalletButton /> }
        </VFlex>
      </Box>
    </VFlex>
    )}

    <VFlex sx={{ mt: 5, flexGrow: 1 }}>
      <BirthdayCountdown countdown={countdown} showLabel={dimStep.step > dimStep.breaks.countdownLabel} showTitle={dimStep.step > dimStep.breaks.countdown} />
        {birthdayLocal && <Typography>{birthdayLocal}</Typography>}
    </VFlex>

    <VFlex className={dimStep.step > dimStep.breaks.h3 ? 'none' : 'fg-fade' }>
      <Typography sx={{mb: 3}} align="center">
        Free NFT drop on June 12 at midnight UTC exactly.
      </Typography>
      <Button variant="outlined" onClick={addToGoogleCal}>Add to Google Calendar</Button>
      <Button variant="text" onClick={() => navigate('/nft/past')} sx={{ mt: 1.5 }}>See past birthday NFTs</Button>
    </VFlex>

    <PreloadVideo />
  </VFlex>
}

export default observer(Home);
